/**
 * Mobile Drawer Navigation Items Component
 *
 * Renders navigation items with proper grouping, nesting,
 * and interaction handling for the mobile drawer.
 */

import Link from "next/link";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import { NavigationItem } from "../types";
import { GroupedNavigationItems, BreadcrumbItem } from "../mobile-drawer-types";

interface MobileDrawerNavigationItemsProps {
  navigationStack: BreadcrumbItem[];
  currentLevel: BreadcrumbItem;
  groupedItems: GroupedNavigationItems;
  pathname: string;
  onItemClick: (item: NavigationItem) => void;
  onClose: () => void;
}

export function MobileDrawerNavigationItems({
  navigationStack,
  currentLevel,
  groupedItems,
  pathname,
  onItemClick,
  onClose,
}: MobileDrawerNavigationItemsProps) {
  const renderNavigationItem = (item: NavigationItem, key: string) => {
    const Icon = item.icon;
    const isCurrent = pathname.startsWith(item.href);
    const hasChildren = item.children && item.children.length > 0;

    return (
      <div
        key={key}
        onClick={() => (hasChildren ? onItemClick(item) : undefined)}
        className={cn(
          "flex items-center justify-between px-4 py-3 text-left rounded-lg transition-all duration-200 transform active:scale-95",
          // iPad-friendly touch targets
          "min-h-[48px] md:min-h-[52px]",
          hasChildren && "cursor-pointer",
          isCurrent
            ? "bg-blue-50 text-blue-700 border border-blue-200 shadow-sm dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800"
            : "text-gray-700 dark:text-gray-300 hover:bg-accent active:bg-accent/80 hover:shadow-sm"
        )}
      >
        {hasChildren ? (
          // Item with children - navigate to submenu
          <>
            <div className="flex items-center min-w-0 flex-1">
              {Icon && <Icon className="mr-3 h-5 w-5 shrink-0" />}
              <span className="font-medium truncate">{item.name}</span>
            </div>
            <ChevronRight className="h-4 w-4 text-gray-400 shrink-0 ml-2" />
          </>
        ) : (
          // Regular navigation link
          <Link href={item.href} onClick={onClose} className="flex items-center w-full min-w-0">
            {Icon && <Icon className="mr-3 h-5 w-5 shrink-0" />}
            <span className="font-medium truncate">{item.name}</span>
          </Link>
        )}
      </div>
    );
  };

  // For root level, use groups. For deeper levels, show items directly
  if (navigationStack.length === 1) {
    const { orderedGroups, ungrouped } = groupedItems;

    return (
      <div className="w-full">
        {/* Render grouped items */}
        {orderedGroups.map((group) => (
          <div key={group.category} className="mb-3">
            <div className="px-4 pt-3 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {group.category}
            </div>
            <div className="space-y-0.5">
              {group.items.map((item) => renderNavigationItem(item, item.id))}
            </div>
          </div>
        ))}

        {/* Render ungrouped items directly without a group header */}
        {ungrouped.length > 0 && (
          <div className="space-y-0.5">
            {ungrouped.map((item) => renderNavigationItem(item, item.id))}
          </div>
        )}
      </div>
    );
  } else {
    // For deeper levels, show items directly without grouping
    return (
      <div className="w-full space-y-0.5">
        {currentLevel.items.map((item) => renderNavigationItem(item, item.id))}
      </div>
    );
  }
}
